import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { ConfirmedAddOns } from 'utils/addOnEligibility';

import GlobalErrorBoundary from 'components/GlobalErrorBoundary/GlobalErrorBoundary';

import { TableHistoryProvider } from './TableHistoryContext';
import { HandHistoryProvider } from './HandHistoryContext';
import { TexasChatContextProvider } from './TexasChatContext';

type GameContextType = {
  preselectedAction: string;
  potPosition: any;
  seatPositions: SeatPosition[];
  betPositions: SeatPosition[];
  tableCards: string[];
  isBlindPosted: boolean;
  playerMultiRunVote: string;
  openAddChips: boolean;
  openAddOnChips: boolean;
  // EN-11059: the add-on count the engine confirmed in its last gotTnmAddon
  // reply on this table. gotTnmStatus is not re-pushed on a purchase, so this
  // is what hides the Add-On button straight after the last add-on is bought.
  confirmedAddOns: ConfirmedAddOns | null;
  canRebuyChips: boolean;
  openRebuyChips: boolean;
  isPlayerReady: boolean;
  isSatOut: boolean;
  isStandingUp: boolean;
  // An "I'm Back" press is held or in flight. Set when the player presses while
  // the table is assigning the dealer button and blinds (the engine silently
  // refuses `returnTable` there), and cleared once the seat actually leaves the
  // paused statuses or the backstop in useDeferredImBack gives up. While it is
  // true the sit-out overlay stays hidden, so the press looks like it worked
  // instead of leaving a dead button on screen. See hooks/useImBack.ts.
  pendingImBack: boolean;
  isTimerOff: boolean;
  showTournamentDetails: boolean;
  dontAskAgain: boolean;
  // EN-10420: true while the bet/raise tray (RaiseActionExpanded) is mounted.
  // Set by the tray itself, read by the POV seat to lift its hole cards clear
  // of the tray. Lives here because the provider is per-table, so one table's
  // open tray can never move another table's cards.
  isBetTrayOpen: boolean;
  setShowTournamentDetails: (value: boolean) => void;
  setPreselectedAction: Dispatch<SetStateAction<string>>;
  setPotPosition: (value: any) => void;
  setSeatPositions: (value: SeatPosition[]) => void;
  setBetPositions: (value: SeatPosition[]) => void;
  setTableCards: (value: string[]) => void;
  setIsBlindPosted: (value: boolean) => void;
  setPlayerMultiRunVote: (value: string) => void;
  setIsPlayerReady: (value: boolean) => void;
  setOpenAddChips: (value: boolean) => void;
  setOpenAddOnChips: (value: boolean) => void;
  setConfirmedAddOns: (value: ConfirmedAddOns | null) => void;
  setCanRebuyChips: (value: boolean) => void;
  setOpenRebuyChips: (value: boolean) => void;
  setIsSatOut: (value: boolean) => void;
  setIsStandingUp: (value: boolean) => void;
  setPendingImBack: (value: boolean) => void;
  setIsTimerOff: (value: boolean) => void;
  setDontAskAgain: (value: boolean) => void;
  setIsBetTrayOpen: (value: boolean) => void;
};

const GameContext = createContext<GameContextType>({
  preselectedAction: '',
  potPosition: { left: 0, top: 0 },
  seatPositions: [],
  betPositions: [],
  tableCards: [],
  isBlindPosted: false,
  playerMultiRunVote: '0',
  openAddChips: false,
  openAddOnChips: false,
  confirmedAddOns: null,
  canRebuyChips: false,
  openRebuyChips: false,
  isPlayerReady: false,
  isSatOut: false,
  isStandingUp: false,
  pendingImBack: false,
  isTimerOff: false,
  showTournamentDetails: false,
  dontAskAgain: false,
  isBetTrayOpen: false,
  setShowTournamentDetails: () => {},
  setPreselectedAction: () => {},
  setPotPosition: () => {},
  setSeatPositions: () => {},
  setBetPositions: () => {},
  setTableCards: () => {},
  setIsBlindPosted: () => {},
  setPlayerMultiRunVote: () => {},
  setOpenAddChips: () => {},
  setOpenAddOnChips: () => {},
  setConfirmedAddOns: () => {},
  setCanRebuyChips: () => {},
  setOpenRebuyChips: () => {},
  setIsPlayerReady: () => {},
  setIsSatOut: () => {},
  setIsStandingUp: () => {},
  setPendingImBack: () => {},
  setIsTimerOff: () => {},
  setDontAskAgain: () => {},
  setIsBetTrayOpen: () => {}
});

type GameProviderProps = PropsWithChildren<{
  handHistoryTableId?: string;
  /**
   * 'demo' for a table that is not live — a hand-history table snapshot —
   * so its nested hand-history provider opens no socket listeners and keeps no
   * session state for a table nobody is seated at.
   */
  handHistorySource?: 'live' | 'demo';
}>;

export const GameProvider = withRouter((props: GameProviderProps) => {
  const { children, handHistoryTableId, handHistorySource } = props;
  const [preselectedAction, setPreselectedAction] = useState('');
  const [potPosition, setPotPosition] = useState({ left: 0, top: 0 });
  const [seatPositions, setSeatPositions] = useState<SeatPosition[]>([]);
  const [betPositions, setBetPositions] = useState<SeatPosition[]>([]);
  const [tableCards, setTableCards] = useState<string[]>([]);
  const [isBlindPosted, setIsBlindPosted] = useState(false);
  const [playerMultiRunVote, setPlayerMultiRunVote] = useState('0');
  const [openAddChips, setOpenAddChips] = useState(false);
  const [openAddOnChips, setOpenAddOnChips] = useState(false);
  const [confirmedAddOns, setConfirmedAddOns] = useState<ConfirmedAddOns | null>(null);
  const [canRebuyChips, setCanRebuyChips] = useState(false);
  const [openRebuyChips, setOpenRebuyChips] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isSatOut, setIsSatOut] = useState(false);
  const [isStandingUp, setIsStandingUp] = useState(false);
  const [pendingImBack, setPendingImBack] = useState(false);
  const [isTimerOff, setIsTimerOff] = useState(false);
  const [showTournamentDetails, setShowTournamentDetails] = useState(false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [isBetTrayOpen, setIsBetTrayOpen] = useState(false);

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes. withRouter re-renders this provider on EVERY route
  // change, and an inline value object gave every consumer a fresh identity
  // each time - a full-tree re-render cascade per navigation/parent render.
  // Setters are useState dispatchers (stable), so only values are deps. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({
      preselectedAction,
      potPosition,
      seatPositions,
      betPositions,
      tableCards,
      isBlindPosted,
      playerMultiRunVote,
      openAddChips,
      openAddOnChips,
      confirmedAddOns,
      canRebuyChips,
      openRebuyChips,
      isPlayerReady,
      isSatOut,
      isStandingUp,
      pendingImBack,
      isTimerOff,
      showTournamentDetails,
      dontAskAgain,
      isBetTrayOpen,
      setShowTournamentDetails,
      setPreselectedAction,
      setPotPosition,
      setSeatPositions,
      setBetPositions,
      setTableCards,
      setIsBlindPosted,
      setPlayerMultiRunVote,
      setOpenAddChips,
      setOpenAddOnChips,
      setConfirmedAddOns,
      setCanRebuyChips,
      setOpenRebuyChips,
      setIsPlayerReady,
      setIsSatOut,
      setIsStandingUp,
      setPendingImBack,
      setIsTimerOff,
      setDontAskAgain,
      setIsBetTrayOpen
    }),
    [
      preselectedAction,
      potPosition,
      seatPositions,
      betPositions,
      tableCards,
      isBlindPosted,
      playerMultiRunVote,
      openAddChips,
      openAddOnChips,
      confirmedAddOns,
      canRebuyChips,
      openRebuyChips,
      isPlayerReady,
      isSatOut,
      isStandingUp,
      pendingImBack,
      isTimerOff,
      showTournamentDetails,
      dontAskAgain,
      isBetTrayOpen
    ]
  );

  const content = (
    <TexasChatContextProvider>
      <GlobalErrorBoundary>{children}</GlobalErrorBoundary>
    </TexasChatContextProvider>
  );

  // Only a real table panel owns a hand-history session. Without a table id the
  // provider would follow state.currentTable (the lobby, snapshots, demos) and
  // write that table's session alongside the panel that owns it.
  return (
    <GameContext.Provider value={value}>
      <TableHistoryProvider>
        {handHistoryTableId ? (
          <HandHistoryProvider tableId={handHistoryTableId} source={handHistorySource}>
            {content}
          </HandHistoryProvider>
        ) : (
          content
        )}
      </TableHistoryProvider>
    </GameContext.Provider>
  );
});

export default GameContext;

function withRouter(Component: any) {
  function ComponentWithRouterProp(props: any) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  }

  return ComponentWithRouterProp;
}
